import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListSubheader from '@mui/material/ListSubheader';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import type { ConfigApi } from '@eis/insuranceservice-sdk';
import { StatusChip } from '@eis/patterns';
import { bareEnum, versionNameOf } from '../model/types';
import { groupVersionsByMajor, issueCountOf, lifecycleOf } from '../model/version-list';

/** `…/fahrrad/2026/1.2` → `1.2`; empty for no baseline. */
const nameOfErn = (ern: string | undefined): string =>
  ern === undefined || ern === '' ? '' : ern.slice(ern.lastIndexOf('/') + 1);

/**
 * The versions of one generation (SDD-016 D-V10): grouped by major, minors nested below in numeric
 * order, newest major first, open drafts on top; a status chip per row, an issue count per draft.
 *
 * Drawn as design v2's left column (prompt 24 Phase 24.4): section bands "Entwürfe" and
 * "Hauptversion N"; a minor row shows its number in mono, the release type and what it is based
 * on; a draft row shows NO number — under SDD-016 a version is numbered only at publish (decision
 * 7), so it reads "Entwurf, basiert auf 1.3" with its open-issue count. The selected row is tinted.
 *
 * Each row is a button whose accessible NAME is the version's number (a draft: "Entwurf …"), so
 * "switch to version 1.1" means pressing the button called 1.1 — the chips sit beside it, not in
 * its name. Shared by the tarif and the group tarif generation screens.
 */
export function VersionList({
  versions,
  generation,
  selectedErn,
  onSelect,
}: {
  readonly versions: readonly ConfigApi.TarifVersion[];
  readonly generation: ConfigApi.Generation | undefined;
  readonly selectedErn: string | undefined;
  readonly onSelect: (ern: string) => void;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const groups = groupVersionsByMajor(versions);

  return (
    <List aria-label={t('tarifs.versions.listLabel')} disablePadding>
      {groups.map((group) => (
        <li key={group.major ?? 'drafts'}>
          <List disablePadding>
            <ListSubheader
              disableSticky
              sx={{
                lineHeight: 2.75,
                px: 2,
                fontWeight: 600,
                color: 'text.primary',
                bgcolor: 'background.default',
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              {group.major === undefined
                ? t('tarifs.versions.draftsGroup')
                : t('tarifs.versions.majorGroup', { major: group.major })}
            </ListSubheader>
            {group.versions.map((version) => {
              const ern = version.ern ?? '';
              const lifecycle = lifecycleOf(version, generation);
              const isDraft = lifecycle === 'DRAFT';
              const name = isDraft
                ? `${t('tarifs.versions.draftLabel')} ${version.draftId ?? ''}`
                : versionNameOf(version);
              const issues = issueCountOf(version);
              const basedOn = nameOfErn(version.basedOnErn);
              const releaseType = bareEnum(version.releaseType, 'RELEASE_TYPE_') || 'UNSPECIFIED';
              const selected = ern === selectedErn;
              return (
                <ListItem
                  key={ern}
                  disablePadding
                  sx={{ borderBottom: 1, borderColor: 'surfaces.hairline' }}
                >
                  <ListItemButton
                    selected={selected}
                    aria-label={name}
                    aria-pressed={selected}
                    onClick={() => {
                      onSelect(ern);
                    }}
                    sx={{
                      px: 2,
                      py: 1.25,
                      gap: 1.5,
                      alignItems: 'center',
                      borderRadius: 0,
                      '&.Mui-selected, &.Mui-selected:hover': { bgcolor: 'surfaces.selected' },
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        minWidth: 28,
                        color: selected ? 'primary.main' : 'text.primary',
                      }}
                    >
                      {isDraft ? '–' : versionNameOf(version)}
                    </Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {isDraft
                          ? basedOn === ''
                            ? t('tarifs.versions.draftRowNoBase')
                            : t('tarifs.versions.draftRow', { version: basedOn })
                          : t(`tarifs.releaseType.${releaseType}` as never)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        {isDraft
                          ? [
                              issues > 0 ? t('tarifs.versions.issues', { count: issues }) : '',
                              t('tarifs.versions.numberAtPublish'),
                            ]
                              .filter(Boolean)
                              .join(' · ')
                          : basedOn === ''
                            ? '—'
                            : t('tarifs.versions.basedOnShort', { version: basedOn })}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                      <StatusChip
                        status={lifecycle}
                        label={t(`tarifs.versions.statusValue.${lifecycle}` as never)}
                      />
                    </Stack>
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </li>
      ))}
    </List>
  );
}
